import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { AssetType, Item, ItemType } from "@thenounproject/lingo-core";

import { ExportContext } from "./types";
import FileExportMenu from "./FileExportMenu";
import ColorExportMenu from "./ColorExportMenu";
import { useFileExport } from "./hooks";
import { useMultiAssetDownload } from "../../components/inspector/use/MultiAssetDownload";
import { Inspectable } from "@constants/Inspector";

type Props = {
  context: ExportContext | null;
  onClose: () => void;
};

// Helper to create a pseudo-Item for asset-only contexts (like Asset Library)
const assetToItem = (inspectable: Inspectable): Item => {
  const { asset } = inspectable;
  return {
    id: asset.id,
    shortId: asset.id,
    kitId: "",
    spaceId: asset.spaceId,
    urlId: asset.id,
    asset: asset,
    type: ItemType.asset,
    status: "active",
    version: 0,
    displayOrder: 0,
    dateAdded: asset.dateAdded,
    dateUpdated: asset.dateUpdated,
    data: {
      backgroundColor: asset.meta?.backgroundColor || "",
    },
  } as Item;
};

/**
 * Smart controller component that analyzes export context and decides what to do
 *
 * Decision flow:
 * 1. No context? → Render nothing
 * 2. Multiple items selected? → Execute batch download, close
 * 3. Single color asset? → Render ColorExportMenu
 * 4. Single URL asset? → Navigate to URL, close
 * 5. Single file with filecuts? → Render FileExportMenu
 * 6. Single file without filecuts? → Execute direct download, close
 */
const AssetExportController: React.FC<Props> = ({ context, onClose }) => {
  const downloadTriggeredRef = useRef(false);

  // Reset the ref when context changes
  useEffect(() => {
    downloadTriggeredRef.current = false;
  }, [context]);
  const multiDownloadHook = useMultiAssetDownload(context?.selectedInspectables || []);
  const downloadMultipleAssets = multiDownloadHook.downloadAssets;

  // Get the item for download hook - create pseudo-item if we only have an asset
  const targetItem = useMemo(() => {
    if (!context?.targetInspectable) return {} as Item;
    const { item } = context.targetInspectable;
    return item || assetToItem(context.targetInspectable);
  }, [context?.targetInspectable]);

  const { downloadAsset: downloadSingleAsset } = useFileExport(targetItem);

  const executeDirectDownload = useCallback(
    (item: Item) => {
      if (downloadTriggeredRef.current) return;
      downloadTriggeredRef.current = true;

      const asset = item.asset;

      // Handle URL assets - open in new tab
      if (asset.type === AssetType.URL) {
        const url = asset.meta.content?.url;
        if (url) {
          window.open(url, "_blank");
        }
        onClose();
        return;
      }

      // Handle file assets without filecuts - direct download
      if (!asset.meta.filecuts) {
        void downloadSingleAsset(asset.type, "1x");
        onClose();
        return;
      }

      onClose();
    },
    [downloadSingleAsset, onClose]
  );

  // Execute multi-asset download
  const executeMultiDownload = useCallback(() => {
    if (downloadTriggeredRef.current) return;
    downloadTriggeredRef.current = true;

    if (downloadMultipleAssets) {
      void downloadMultipleAssets();
    }
    onClose();
  }, [downloadMultipleAssets, onClose]);

  // Main decision logic
  useEffect(() => {
    if (!context) return;

    const { targetInspectable, selectedInspectables } = context;
    const asset = targetInspectable.asset;

    // Case 1: Color asset
    if (asset.type === AssetType.color) {
      // ColorExportMenu will be rendered below
      return;
    }

    // Case 2: Multiple downloadable (non-color) assets selected → Execute multi-download
    // Only count inspectables that are actually assets and not color assets for multi-download decision
    const downloadableAssets = selectedInspectables.filter(inspectable => {
      const asset = inspectable.asset;
      return asset && asset.type !== AssetType.color;
    });
    const isMultiDownload = downloadableAssets.length > 1;
    if (isMultiDownload) {
      executeMultiDownload();
      return;
    }

    // Case 3: Single URL asset → Navigate immediately
    if (asset.type === AssetType.URL) {
      executeDirectDownload(targetItem);
      return;
    }

    // Case 4: Single file without filecuts → Direct download
    if (!asset.meta.filecuts) {
      executeDirectDownload(targetItem);
      return;
    }

    // Case 5: Single file with filecuts → Show menu
    // FileExportMenu will be rendered below
  }, [context, executeDirectDownload, executeMultiDownload, targetItem]);

  // If no context, render nothing
  if (!context) {
    return null;
  }

  const { coordinates, canEdit, targetInspectable } = context;
  const asset = targetInspectable.asset;

  // Calculate popup position
  const position = {
    xPos: `${coordinates.x}px`,
    yPos: `${coordinates.y}px`,
  };

  // Render color menu for color assets
  if (asset.type === AssetType.color) {
    return <ColorExportMenu item={targetItem} close={onClose} position={position} />;
  }

  // Render file export menu for files with filecuts
  if (asset.meta.filecuts) {
    return (
      <FileExportMenu item={targetItem} canEdit={canEdit} close={onClose} position={position} />
    );
  }

  // All other cases should have been handled in useEffect
  return null;
};

export default AssetExportController;
